import type { Contact } from "@/lib/types/Contact";
import type { LayoutField } from "./SignatureRequestPdfLayoutEditor.constants";
import {
  isContactSigner,
  isUserSigner,
  type SignatureRequestSigner,
} from "./signatureRequestSigners";

export type MatterSignatureRequestFieldPayload = {
  matterSignatureRequestFieldHeight: number;
  matterSignatureRequestFieldLabel: string;
  matterSignatureRequestFieldPage: number;
  matterSignatureRequestFieldRequired: boolean;
  matterSignatureRequestFieldSignerId: string;
  matterSignatureRequestFieldSignerType: "contact" | "user";
  matterSignatureRequestFieldType: LayoutField["type"];
  matterSignatureRequestFieldWidth: number;
  matterSignatureRequestFieldX: number;
  matterSignatureRequestFieldY: number;
};

export type FirmSignatureRequestFieldPayload = {
  fontFamily?: LayoutField["fontFamily"];
  fontSize?: number;
  height: number;
  label: string;
  page: number;
  required: boolean;
  signerId: string;
  signerType: "contact" | "user";
  type: LayoutField["type"];
  width: number;
  x: number;
  y: number;
};

export type MatterSignatureRequestV2FieldPayload =
  FirmSignatureRequestFieldPayload;

function buildSignatureRequestFieldsPayload(
  layoutFields: LayoutField[],
  signers: SignatureRequestSigner[],
): MatterSignatureRequestFieldPayload[] | null {
  const signerKindById = new Map<string, "contact" | "user">();

  signers.forEach((signer) => {
    if (isContactSigner(signer)) {
      signerKindById.set(signer.contact.id, "contact");
      return;
    }
    if (isUserSigner(signer)) {
      signerKindById.set(signer.user.id, "user");
    }
  });

  const fieldsPayload: MatterSignatureRequestFieldPayload[] = [];
  for (const field of layoutFields) {
    const signerType = signerKindById.get(field.signerId);

    if (!signerType) {
      return null;
    }

    fieldsPayload.push({
      matterSignatureRequestFieldHeight: field.height,
      matterSignatureRequestFieldLabel: field.label,
      matterSignatureRequestFieldPage: field.page,
      matterSignatureRequestFieldRequired: true,
      matterSignatureRequestFieldSignerId: field.signerId,
      matterSignatureRequestFieldSignerType: signerType,
      matterSignatureRequestFieldType: field.type,
      matterSignatureRequestFieldWidth: field.width,
      matterSignatureRequestFieldX: field.x,
      matterSignatureRequestFieldY: field.y,
    });
  }

  return fieldsPayload;
}

export function buildMatterSignatureRequestFieldsPayload(
  layoutFields: LayoutField[],
  signers: SignatureRequestSigner[],
) {
  return buildSignatureRequestFieldsPayload(layoutFields, signers);
}

export function buildFirmSignatureRequestFieldsPayload(
  layoutFields: LayoutField[],
  signers: SignatureRequestSigner[],
): FirmSignatureRequestFieldPayload[] | null {
  const fields = buildSignatureRequestFieldsPayload(layoutFields, signers);
  if (!fields) {
    return null;
  }

  return fields.map((field, index) => ({
    fontFamily: layoutFields[index].fontFamily,
    fontSize: layoutFields[index].fontSize,
    height: field.matterSignatureRequestFieldHeight,
    label: field.matterSignatureRequestFieldLabel,
    page: field.matterSignatureRequestFieldPage,
    required: field.matterSignatureRequestFieldRequired,
    signerId: field.matterSignatureRequestFieldSignerId,
    signerType: field.matterSignatureRequestFieldSignerType,
    type: field.matterSignatureRequestFieldType,
    width: field.matterSignatureRequestFieldWidth,
    x: field.matterSignatureRequestFieldX,
    y: field.matterSignatureRequestFieldY,
  }));
}

export function buildMatterSignatureRequestV2FieldsPayload(
  layoutFields: LayoutField[],
  signers: SignatureRequestSigner[],
): MatterSignatureRequestV2FieldPayload[] | null {
  return buildFirmSignatureRequestFieldsPayload(layoutFields, signers);
}

export function buildSignatureRequestNotificationWarningDescription(
  deliveryMethod: "email" | "text" | "both",
  contactSigners: Array<Contact | undefined>,
) {
  const hasContactsMissingEmail = contactSigners.some(
    (contact) => contact?.emails.length === 0,
  );
  const hasContactsMissingPhone = contactSigners.some(
    (contact) => contact?.phones.length === 0,
  );

  if (deliveryMethod === "email" && hasContactsMissingEmail) {
    return "Some recipients don’t have an email address. You’ll need to send the signing link to them manually. Would you like to continue?";
  }

  if (deliveryMethod === "text" && hasContactsMissingPhone) {
    return "Some recipients don’t have a phone number. You’ll need to send the signing link to them manually. Would you like to continue?";
  }

  if (deliveryMethod === "both") {
    if (hasContactsMissingEmail && hasContactsMissingPhone) {
      return "Some recipients don’t have an email address or phone number. You’ll need to send the signing link to them manually. Would you like to continue?";
    }

    if (hasContactsMissingEmail) {
      return "Some recipients don’t have an email address. They’ll only receive the signing link by text. Would you like to continue?";
    }

    if (hasContactsMissingPhone) {
      return "Some recipients don’t have a phone number. They’ll only receive the signing link by email. Would you like to continue?";
    }
  }

  return null;
}
